import MoreVertIcon from "@mui/icons-material/MoreVert";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { confirm } from "material-ui-confirm";
import { memo, MouseEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import useFetch from "../../hooks/useFetch";
import { IExams } from "../../models/exams";
import { emptyManagementExamReleaseDate } from "../../services/exams.service";
import { useAppSelector } from "../../store/store";

const translationPath = "page.exams.";

interface IManagementLaboratorialExamsRowActionsProps {
  exam: IExams;
  onEmptyReleaseDateSuccess: () => void;
}


const EXAM_MANAGER_PROFILE_CODE = "11";

const ManagementLaboratorialExamsRowActions: React.FC<
  IManagementLaboratorialExamsRowActionsProps
> = ({ exam, onEmptyReleaseDateSuccess }) => {
  const { t } = useTranslation();
  const { individual } = useAppSelector((state) => state.user);
  const { sendRequest, loading } = useFetch(emptyManagementExamReleaseDate);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const hasExamManagerProfile = individual.profiles.some(
    (profile) => profile.code === EXAM_MANAGER_PROFILE_CODE,
  );
  const canEmptyReleaseDate =
    exam.type !== "LABORATORIAL" && !!exam.sequencialControl && hasExamManagerProfile;

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleEmptyReleaseDate = useCallback(async () => {
    if (!canEmptyReleaseDate) return;

    handleClose();

    try {
      await confirm({
        title: t(translationPath + "emptyReleaseDate.title"),
        description: t(translationPath + "emptyReleaseDate.description"),
        confirmationText: t(
          translationPath + "emptyReleaseDate.confirmationText",
        ),
        cancellationText: t(
          translationPath + "emptyReleaseDate.cancellationText",
        ),
      });

      const { success } = await sendRequest(exam.sequencialControl);

      if (success) {
        onEmptyReleaseDateSuccess();
      }
    } catch (error) {}
  }, [
    canEmptyReleaseDate,
    exam.sequencialControl,
    handleClose,
    onEmptyReleaseDateSuccess,
    sendRequest,
    t,
  ]);

  return (
    <>
      <Tooltip title={t(translationPath + "actions")}>
        <span>
          <IconButton size="small" onClick={handleOpen} disabled={loading}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={handleEmptyReleaseDate}
          disabled={!canEmptyReleaseDate || loading}
        >
          <ListItemIcon>
            <UndoRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t(translationPath + "emptyReleaseDate.action")}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default memo(ManagementLaboratorialExamsRowActions);
